import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, useSearch } from "wouter";
import { useHistoryState } from "wouter/use-browser-location";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import type { Invoice, Trip, TripPaymentStatus } from "@shared/schema";
import TripDetailsCard from "@/components/invoicing/TripDetailsCard";
import TripInvoiceList from "@/components/invoicing/TripInvoiceList";
import { useInvoicesByTrip } from "@/hooks/useInvoices";
import { FINANCES_PATHS } from "@/lib/financesPaths";
import { useAuth } from "@/context/AuthContext";
import { useImpersonation } from "@/context/ImpersonationContext";
import { useCurrentTramsContext, useSelectTramsAccount } from "@/hooks/useTramsAccounts";
import { TramsAccountSwitcher } from "@/components/trams/TramsAccountSwitcher";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";

interface TripInvoiceSummary {
  tripId: number;
  invoiceCount: number;
  paymentStatus: TripPaymentStatus;
}

interface TripEditNavState {
  highlightInvoiceId?: number;
  tramsAccountId?: number;
}

function parseOptionalInt(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const parsed = typeof value === "number" ? value : parseInt(String(value), 10);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export default function EditTrip() {
  const params = useParams<{ tripId: string }>();
  const tripId = params?.tripId ? parseInt(params.tripId, 10) : undefined;
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const navState = useHistoryState() as TripEditNavState | null;
  const { user } = useAuth();
  const { impersonation } = useImpersonation();
  const { data: tramsData } = useCurrentTramsContext();
  const currentTramsAccountId = tramsData?.context?.tramsAccountId;
  const selectTramsAccount = useSelectTramsAccount();
  const appliedTramsRef = useRef<number | null>(null);
  const [tripDirty, setTripDirty] = useState(false);

  const { requestLeave } = useUnsavedChangesGuard(() => tripDirty);

  const handleBack = () => {
    requestLeave(() => setLocation(FINANCES_PATHS.invoices));
  };

  const searchParams = useMemo(
    () => new URLSearchParams(searchString),
    [searchString],
  );

  const highlightInvoiceId = useMemo(
    () =>
      parseOptionalInt(searchParams.get("invoiceId")) ??
      parseOptionalInt(navState?.highlightInvoiceId),
    [searchParams, navState?.highlightInvoiceId],
  );

  const tramsAccountIdFromUrl = useMemo(
    () =>
      parseOptionalInt(searchParams.get("tramsAccountId")) ??
      parseOptionalInt(navState?.tramsAccountId),
    [searchParams, navState?.tramsAccountId],
  );

  const { data: highlightInvoice } = useQuery<Invoice>({
    queryKey: [`/api/invoices/${highlightInvoiceId}`],
    enabled: !!highlightInvoiceId,
  });

  const targetTramsAccountId =
    tramsAccountIdFromUrl ?? highlightInvoice?.tramsAccountId ?? undefined;

  const effectiveTramsAccountId =
    targetTramsAccountId ?? currentTramsAccountId;

  const scopedHighlightInvoiceId = useMemo(() => {
    if (!highlightInvoiceId) return undefined;
    if (!highlightInvoice) return highlightInvoiceId;
    const invTrams = highlightInvoice.tramsAccountId;
    if (invTrams == null) return highlightInvoiceId;
    if (effectiveTramsAccountId == null) return highlightInvoiceId;
    return invTrams === effectiveTramsAccountId ? highlightInvoiceId : undefined;
  }, [
    highlightInvoiceId,
    highlightInvoice,
    effectiveTramsAccountId,
  ]);

  useEffect(() => {
    appliedTramsRef.current = null;
  }, [tramsAccountIdFromUrl]);

  useEffect(() => {
    if (targetTramsAccountId == null) return;
    if (targetTramsAccountId === currentTramsAccountId) return;
    if (appliedTramsRef.current === targetTramsAccountId) return;
    appliedTramsRef.current = targetTramsAccountId;
    selectTramsAccount.mutate({ tramsAccountId: targetTramsAccountId });
  }, [targetTramsAccountId, currentTramsAccountId, selectTramsAccount]);

  const handleTramsAccountChange = () => {
    appliedTramsRef.current = null;
    if (!tripId) return;
    setLocation(FINANCES_PATHS.tripEdit(tripId), { replace: true });
  };

  const effectiveUserId =
    impersonation.active && impersonation.targetUserId
      ? impersonation.targetUserId
      : user?.id;

  const {
    data: trip,
    isLoading: tripLoading,
    error: tripError,
  } = useQuery<Trip>({
    queryKey: [`/api/trips/${tripId}`],
    enabled: !!tripId,
  });

  const { data: summary } = useQuery<TripInvoiceSummary>({
    queryKey: [
      `/api/trips/${tripId}/invoices/summary`,
      { tramsAccountId: effectiveTramsAccountId, highlightInvoiceId: scopedHighlightInvoiceId },
    ],
    enabled: !!tripId,
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (effectiveTramsAccountId != null) {
        qs.set("tramsAccountId", String(effectiveTramsAccountId));
      }
      if (scopedHighlightInvoiceId != null) {
        qs.set("highlightInvoiceId", String(scopedHighlightInvoiceId));
      }
      const query = qs.toString();
      const res = await fetch(
        `/api/trips/${tripId}/invoices/summary${query ? `?${query}` : ""}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        throw new Error("Failed to fetch trip summary");
      }
      return res.json();
    },
  });

  const {
    data: invoices = [],
    isLoading: invoicesLoading,
  } = useInvoicesByTrip(tripId, effectiveTramsAccountId, scopedHighlightInvoiceId);

  const canEditTrip =
    !!trip &&
    !!effectiveUserId &&
    (trip.advisorId === effectiveUserId ||
      trip.createdBy === effectiveUserId ||
      user?.role === "SYSTEM_ADMIN");

  if (!tripId || Number.isNaN(tripId)) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-lg font-medium">Invalid trip</p>
        <Button variant="outline" onClick={() => setLocation(FINANCES_PATHS.invoices)}>
          Back to Invoices
        </Button>
      </div>
    );
  }

  if (tripLoading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading trip...
      </div>
    );
  }

  if (tripError || !trip) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-lg font-medium">Trip not found</p>
        <Button variant="outline" onClick={() => setLocation(FINANCES_PATHS.invoices)}>
          Back to Invoices
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 border-b bg-background px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex flex-col gap-3 max-w-6xl mx-auto sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <h1 className="text-xl font-semibold truncate">Edit Trip — {trip.name}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:shrink-0">
            <TramsAccountSwitcher onAccountChange={handleTramsAccountChange} />
            <Button
              variant="outline"
              onClick={() => setLocation(`${FINANCES_PATHS.invoicesNew}?tripId=${tripId}`)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Invoice
            </Button>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <TripDetailsCard
                trip={trip}
                canEdit={canEditTrip}
                paymentStatus={summary?.paymentStatus}
                onDirtyChange={setTripDirty}
              />
            </div>

            <div className="lg:col-span-2 space-y-4">
              <Separator className="lg:hidden" />
              <TripInvoiceList
                tripId={tripId}
                invoices={invoices}
                isLoading={invoicesLoading}
                highlightInvoiceId={scopedHighlightInvoiceId}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
